/**
 * The ingest contract. Mirrors `shared/schemas/platform.ts` in the AI
 * Workbench repository (PLATFORM_SCHEMA_VERSION 1): the workbench validates
 * an envelope against these shapes before it sends, and the AIOE ingest
 * endpoint validates again on receipt. Change both copies together.
 */
import { z } from "zod";
import { runRecordSchema } from "./agentRuns.js";
import { auditSeveritySchema } from "./microsoft365.js";
export const PLATFORM_SCHEMA_VERSION = 1;
export const usageRecordSchema = z.object({
    at: z.string(),
    agentKey: z.string().optional(),
    provider: z.string(),
    model: z.string(),
    promptTokens: z.number().int().nonnegative(),
    completionTokens: z.number().int().nonnegative(),
    exactUsage: z.boolean(),
    costUsd: z.number().nonnegative().optional(),
    latencyMs: z.number().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    status: z.enum(["ok", "error", "cancelled"]),
    repairs: z.number().int().nonnegative().optional(),
    /** The person the call was made for, and the agent identity that made it (from a node). AIOE-first. */
    principal: z.string().max(200).optional(),
    actor: z.string().max(200).optional(),
    /** Where the call belongs: the repository (root), the team and the board task, when known. */
    rootId: z.string().max(200).optional(),
    teamId: z.string().max(200).optional(),
    taskId: z.string().max(100).optional(),
    /**
     * Ledger integrity (AIOE 0.16.0, cost round 1). `id` is made when the record is written (a uuid)
     * and the platform keeps one record per (tenant, id), so a resent envelope never doubles spend.
     * Absent on records from older builds, which are taken as before.
     */
    id: z.string().min(8).max(64).optional(),
    /** What made the call: `claude-code`, `copilot`, `codex`, `acp`, `proxy`, `cloud`; free text. */
    runtime: z.string().max(60).optional(),
    /**
     * What the dollars are. `metered`: the provider bills per token and `costUsd` is spend. `subscription`:
     * a seat already paid for; `costUsd` is the list-price value of the call, not spend, and money
     * budgets do not count it. `local`: a model on the machine, no provider charge. Absent means metered.
     * `azure` (D273): a Foundry member's model round, billed by Azure to the customer's subscription;
     * `costUsd` is the workbench's estimate (`estimated: true`) for live budgets, and the Azure meter
     * import supersedes it by day and model on the platform. The two are never summed.
     */
    billing: z.enum(["metered", "subscription", "local", "azure"]).optional(),
    /** Azure usage (billing "azure"): <resource host>/<project> for Foundry, the host alone for the Azure gateway. */
    azureSource: z.string().max(300).optional(),
    /** Azure Local usage (provider foundry-local, billing local): the cluster's name. */
    cluster: z.string().max(200).optional(),
    /** `costUsd` is an estimate the provider's own bill replaces (D273: `billing: "azure"`). Absent means not. */
    estimated: z.boolean().optional(),
    /**
     * How `costUsd` was arrived at. `provider`: the provider's own figure (Claude Code's total, a usage
     * header). `list`: tokens times a price list on the machine. `none`: no price was known, so `costUsd`
     * is absent and the platform counts the call as unpriced rather than free. Absent means `list`.
     * `platform` (cost round 3, optional): the platform priced it from the organisation's price list
     * on ingest; a workbench never sends it, it reads it on records the platform answers.
     */
    priced: z.enum(["provider", "list", "none", "platform"]).optional(),
    /**
     * Cost round 3 (pricing.ts): the machine's own `costUsd`, kept when the platform priced the record
     * again from its tokens with the organisation's price list (`priced: "platform"`). Set by the
     * platform only: a workbench never sends it (ingest drops what is sent) and reads it back where
     * the platform answers records, to show "your machine said US$x".
     */
    reportedUsd: z.number().nonnegative().optional(),
    /** Prompt-cache tokens, counted INSIDE `promptTokens` (they are part of the prompt); priced at their own rates when the list has them, with the uncached remainder at the input rate. */
    cacheReadTokens: z.number().int().nonnegative().optional(),
    cacheWriteTokens: z.number().int().nonnegative().optional(),
    /** Images the call produced (image models); images in the prompt are already tokens. */
    images: z.number().int().nonnegative().optional(),
    /** What GitHub Copilot bills instead of tokens: premium requests consumed by the call. */
    premiumRequests: z.number().int().nonnegative().optional(),
    /** Credits a vendor charged for the call (Copilot's cloud agent bills in credits): a count beside the spend, never spend itself. */
    aiCredits: z.number().nonnegative().optional(),
    /**
     * Analytics (workbench D401, 0.90.0; all optional, absent on records from older builds): the agent session the call was
     * made in; the member who made it, as `agent:<teamId>/<name>` for a member of a team or `specialist:<specialistId>` for a
     * specialist on its own (`memberKind` says which); the run of that agent it belongs to (agentRuns.ts `runId`); and the
     * workflow run and step it was made for (shared/schemas/workflows.ts). What the agent and team Overview tiles are cut by.
     */
    sessionId: z.string().max(200).optional(),
    memberId: z.string().max(300).optional(),
    memberKind: z.enum(["specialist", "member"]).optional(),
    runId: z.string().max(200).optional(),
    workflowRunId: z.string().max(100).optional(),
    stepId: z.string().max(60).optional(),
});
export const auditEntrySchema = z.object({
    ts: z.string(),
    // Mirrors the workbench's `auditEntrySchema` (branch feat/agent-identity-and-policy-pinning).
    // Every field past `kind` is optional so entries from older hosts still parse.
    actor: z.object({
        kind: z.enum(["user", "remote", "agent", "system", "schedule"]),
        id: z.string().optional(),
        /** `agent`: the ACP session its MCP bridge credential is bound to. */
        sessionId: z.string().optional(),
        teamId: z.string().optional(),
        agentName: z.string().optional(),
        taskId: z.string().optional(),
        /** The person on whose behalf the actor acts, when known. */
        principal: z.string().optional(),
        /** The member's Entra agent identity, when the organisation has given it one (Microsoft 365 A6). */
        entraAgentId: z.string().optional(),
    }),
    action: z.string(),
    target: z.string().optional(),
    teamId: z.string().optional(),
    /** The agent session behind the action, when an agent took it. */
    sessionId: z.string().optional(),
    /** The run ledger's id (agentRuns.ts runId): one id from the workbench through the audit trail to the exports (AIOE 0.40.0). */
    runId: z.string().max(200).optional(),
    /** The task the action belongs to, when known. */
    taskId: z.string().optional(),
    detail: z.record(z.string(), z.unknown()).optional(),
    outcome: z.enum(["ok", "denied", "error"]),
    error: z.string().optional(),
    /** SHA-256 of the previous line in the workbench's daily audit file, or "genesis" — the hash chain. */
    prev: z.string().optional(),
    /** How serious the row is (Microsoft 365 C2), set where it was written (the workbench's auditSeverity.ts). Absent on rows from older hosts. */
    severity: auditSeveritySchema.optional(),
});
export const controlEventSchema = z.object({
    seq: z.number().int().nonnegative(),
    at: z.number(),
    kind: z.string(),
    payload: z.record(z.string(), z.unknown()),
});
export const taskSnapshotSchema = z.object({
    id: z.string(),
    uid: z.string().optional(),
    teamId: z.string(),
    title: z.string(),
    status: z.string(),
    priority: z.string(),
    assignee: z.string().optional(),
    labels: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    outcome: z.string().optional(),
    /** Who dispatched the task, and the agent working it (from a node). AIOE-first. */
    principal: z.string().max(200).optional(),
    actor: z.string().max(200).optional(),
});
/** One upload: everything since the last cursor, in one envelope. */
export const platformEnvelopeSchema = z.object({
    schemaVersion: z.literal(PLATFORM_SCHEMA_VERSION),
    projectId: z.string(),
    workspaceId: z.string(),
    sentAt: z.string(),
    cursors: z.object({
        events: z.number().int().nonnegative(),
        usage: z.string().optional(),
        audit: z.string().optional(),
        runs: z.number().int().nonnegative().optional(),
    }),
    events: z.array(controlEventSchema).default([]),
    usage: z.array(usageRecordSchema).default([]),
    audit: z.array(auditEntrySchema).default([]),
    tasks: z.array(taskSnapshotSchema).default([]),
    /** The run model (Microsoft 365 C1d, ./agentRuns.ts): what the AIOE builds Agent 365 spans from. Absent from older hosts. */
    runs: z.array(runRecordSchema).max(200).default([]),
});
/** What the ingest endpoint answers: the cursors it now holds, so the workbench can advance. */
export const ingestResponseSchema = z.object({
    accepted: z.object({ events: z.number().int(), usage: z.number().int(), audit: z.number().int(), tasks: z.number().int() }),
    cursors: platformEnvelopeSchema.shape.cursors,
});
//# sourceMappingURL=platform.js.map